import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { CoaType } from "@/types/chartOfAccount";

export type Option = { id: number; name: string };

export type ChartFormState = {
  code: string;
  name: string;
  type: CoaType | "";
  category_id: string | number;
};

type Props = {
  form: ChartFormState;
  setForm: React.Dispatch<React.SetStateAction<ChartFormState>>;
  categories: Option[];
  categorySearch: string;
  setCategorySearch: (v: string) => void;
  categoriesLoading?: boolean;
};

const TYPES: CoaType[] = ["Assets", "Liabilities", "Equity", "Revenue", "Expenses"];

export function ChartAccountForm({
  form,
  setForm,
  categories,
  categorySearch,
  setCategorySearch,
  categoriesLoading,
}: Props) {
  const { t } = useTranslation();
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";

  const [catOpen, setCatOpen] = useState(false);

  const selectedCategoryName = useMemo(
    () => categories.find((x) => String(x.id) === String(form.category_id))?.name,
    [categories, form.category_id]
  );

  // clear search when closing dropdown
  useEffect(() => {
    if (!catOpen) setCategorySearch("");
  }, [catOpen, setCategorySearch]);

  return (
    <div className="space-y-4" dir={dir}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Code */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("common.code", "Code")} *</label>
          <Input
            className="rounded-xl"
            value={form.code ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
            placeholder={t("common.code", "Code")}
          />
        </div>

        {/* Name */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("common.name", "Name")} *</label>
          <Input
            className="rounded-xl"
            value={form.name ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            placeholder={t("common.name", "Name")}
          />
        </div>

        {/* Type */}
        <div className="space-y-2 md:col-span-2">
          <Label>Type *</Label>
          <Select
            value={form.type}
            onValueChange={(v) =>
              setForm((p) => ({
                ...p,
                type: v as CoaType,
                category_id: "", // ✅ reset category when type changes
              }))
            }
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Category (depends on type) */}
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs text-muted-foreground">Category *</label>

          <Popover open={catOpen} onOpenChange={setCatOpen} modal={false}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between rounded-xl"
                disabled={!form.type}
              >
                {form.category_id
                  ? selectedCategoryName || t("common.selected", "Selected")
                  : form.type
                  ? "Select Category"
                  : "Select Type first"}
                <ChevronsUpDown className={rtl ? "mr-2 h-4 w-4 opacity-50" : "ml-2 h-4 w-4 opacity-50"} />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 max-h-72 overflow-auto" align="start" sideOffset={8}>
              <Command shouldFilter={false}>
                <CommandInput placeholder={t("common.search", "Search...")} value={categorySearch} onValueChange={setCategorySearch} />
                <CommandEmpty>
                  {categoriesLoading ? t("common.loading", "Loading...") : t("common.no_data", "No data available")}
                </CommandEmpty>

                <CommandGroup>
                  {(categories ?? []).map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.name}
                      onSelect={() => {
                        setForm((prev) => ({ ...prev, category_id: String(c.id) }));
                        setCatOpen(false);
                      }}
                    >
                      <Check
                        className={`${rtl ? "ml-2" : "mr-2"} h-4 w-4 ${
                          String(form.category_id) === String(c.id) ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}